import { useTranslation } from 'next-i18next'
import { useRef, useState } from 'react'
import { v4 as uuid } from 'uuid'

import ActivityWrapper from '~/bootcamp/Activities/components/ActivityWrapper'
import Divider from '~/bootcamp/Activities/components/Divider'
import FractionsActivityWrapper from '~/bootcamp/Activities/Fractions/components/FractionsActivityWrapper'

import useFocus from '~/bootcamp/Activities/hooks/useFocus'
import { ActivityProps } from '~/bootcamp/Activities/types/activities.types'
import Input from '~/bootcamp/Activities/components/Input'
import { hocOnEnterPress } from '~/bootcamp/Activities/utils/activities.utils'
import { useEmitInstance } from '~/bootcamp/Activities/utils/instance.utils'

import useImproperFraction from './useImproperFraction'

function ImproperFraction({
  answerState,
  actionButtonsFC,
  autofocus = true,
  difficultyLevel,
  onAnswerStateChange,
  onInstanceChange,
  onNextQuestionClick,
}: ActivityProps): React.ReactElement {
  const { t } = useTranslation('generics')
  const { numerator, denominator, formValid, onInputChange, handleCheckAnswer, goToNextQuestion } = useImproperFraction(
    {
      difficultyLevel,
      onAnswerStateChange,
      onNextQuestionClick,
    },
  )
  const [inputKey, setInputKey] = useState<string>(uuid())

  const inputRef = useRef<HTMLInputElement>(null)
  useFocus(autofocus, answerState, inputRef)

  // `numerator` is the student's typed answer, not the task.
  useEmitInstance(onInstanceChange, `an improper fraction: \\frac{\\square}{${denominator}}`)

  const handleGoToNextQuestion = (): void => {
    goToNextQuestion()
    setInputKey(uuid())
  }

  const onEnterPress = (): void => {
    if (formValid) {
      hocOnEnterPress(answerState, handleCheckAnswer, handleGoToNextQuestion)
    }
  }

  return (
    <ActivityWrapper>
      <FractionsActivityWrapper>
        <div className="mb-1">
          <Input
            aria-label={t('ariaLabelButtons.insertNumerator')}
            autoFocus={autofocus}
            isError={answerState === false}
            key={inputKey}
            maxDigits={3}
            onChange={(value) => onInputChange(value)}
            onEnter={onEnterPress}
            ref={inputRef}
            value={`${numerator || numerator === 0 ? numerator : ''}`}
          />
        </div>
        <Divider />
        <span className="text-2xl">{denominator}</span>
      </FractionsActivityWrapper>
      {actionButtonsFC &&
        actionButtonsFC({
          formValid,
          correctAnswer: answerState ?? false,
          submitAnswerAction: handleCheckAnswer,
          clearFormAction: () => onInputChange(),
          goToNextQuestionAction: handleGoToNextQuestion,
        })}
    </ActivityWrapper>
  )
}

export default ImproperFraction
